/**
 * Payment reminders card (Settings > Invoicing).
 *
 * Template: templates/payment_reminders/_settings_card.html, with each step row
 * from templates/payment_reminders/_step_row.html. The server parses and
 * validates everything (application/schemas/reminder_settings.py); this only
 * edits the form:
 *
 *   - the master switch shows or hides the settings below it;
 *   - "Add a reminder" copies the blank row out of the card's <template>, giving
 *     it a fresh row id, up to the cap (max value); Remove drops a row;
 *   - choosing "on the due date" disables the row's Days field, which the
 *     schema then ignores;
 *   - Preview posts the row to the preview route and shows the subject and
 *     text as the client will read them.
 *
 * Config: data-reminder-steps-max-value, data-reminder-steps-preview-url-value.
 * Rows are targets, so the add button follows every add and remove, including
 * a repaint of the whole card after a save.
 */
import { Controller } from "@hotwired/stimulus";

const ROW_PLACEHOLDER = "__ROW__";

export default class extends Controller {
  static targets = ["switch", "config", "list", "template", "row", "addButton", "days"];
  static values = { max: Number, previewUrl: String };

  connect() {
    this._added = 0;
  }

  toggle() {
    if (this.hasConfigTarget) this.configTarget.hidden = !this.switchTarget.checked;
  }

  add() {
    if (this.rowTargets.length >= this.maxValue) return;
    this._added += 1;
    const html = this.templateTarget.innerHTML.replaceAll(ROW_PLACEHOLDER, `new-${this._added}`);
    this.listTarget.insertAdjacentHTML("beforeend", html);
  }

  remove(event) {
    this._rowOf(event).remove();
  }

  timing(event) {
    const days = this._rowOf(event).querySelector('[data-reminder-steps-target="days"]');
    days.disabled = event.currentTarget.value === "on";
  }

  async preview(event) {
    const row = this._rowOf(event);
    const output = row.querySelector("[data-preview]");
    output.hidden = false;
    output.textContent = "Loading preview...";
    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        body: this._rowData(row),
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      this._showPreview(output, await response.json());
    } catch {
      output.textContent = "The preview could not be loaded. Check your connection and try again.";
    }
  }

  rowTargetConnected() {
    this._updateAddButton();
  }

  rowTargetDisconnected() {
    this._updateAddButton();
  }

  addButtonTargetConnected() {
    this._updateAddButton();
  }

  _updateAddButton() {
    if (this.hasAddButtonTarget) this.addButtonTarget.disabled = this.rowTargets.length >= this.maxValue;
  }

  _rowOf(event) {
    return event.currentTarget.closest('[data-reminder-steps-target="row"]');
  }

  _rowData(row) {
    const data = new FormData();
    data.append("csrf_token", this.element.querySelector('input[name="csrf_token"]').value);
    row.querySelectorAll("input[name], select[name], textarea[name]").forEach((field) => {
      if (field.disabled || (field.type === "checkbox" && !field.checked)) return;
      data.append(field.name, field.value);
    });
    return data;
  }

  _showPreview(output, data) {
    output.replaceChildren();
    if (data.errors) {
      output.append(this._line("p", "text-danger small mb-0", data.errors.join(" ")));
      return;
    }
    output.append(
      this._line("p", "text-muted small mb-1", data.sample),
      this._line("p", "fw-semibold mb-1", data.subject),
      this._line("div", "reminder-step-preview-body small", data.body),
    );
  }

  _line(tag, className, text) {
    const element = document.createElement(tag);
    element.className = className;
    element.textContent = text;
    return element;
  }
}
